import React from 'react';
import DatePicker from 'react-datepicker';
import { Calendar } from 'react-feather';
import { useTranslation } from 'react-i18next';

/**
 * V3 date field for report filter bars. Replaces the native
 * `<input type="date">` whose calendar popup is the browser's OS-default
 * widget (a white box that clashes with the V3 glass skin).
 *
 * react-datepicker renders its own DOM popup, so it themes via the
 * `.v3-datepicker` overrides in reports.css. The calendar is portalled to
 * document.body (portalId) to escape the filter card's `backdrop-filter`
 * stacking context — same reason MultiSelect portals its menu.
 *
 * Values stay 'YYYY-MM-DD' strings (the format the rest of the report code
 * and the API expect); conversion is local-time-safe (no `new Date(str)`
 * UTC off-by-one).
 *
 * Props mirror the old native input: { id, value, max, disabled, onChange }.
 */
const pad = (n) => String(n).padStart(2, '0');

const toDate = (str) => {
    if (!str) return null;
    const [y, m, d] = String(str).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

const toStr = (date) =>
    (date instanceof Date && !Number.isNaN(date.getTime())
        ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
        : '');

const ReportDateInput = ({ id, value, max, disabled, onChange, placeholder = 'Select date' }) => {
    const { t } = useTranslation();
    return (
        <div className="v3-datepicker-wrap">
            <DatePicker
                id={id}
                selected={toDate(value)}
                onChange={(date) => onChange?.(toStr(date))}
                maxDate={toDate(max)}
                dateFormat="dd/MM/yyyy"
                placeholderText={t(placeholder)}
                disabled={disabled}
                className="v3-input v3-datepicker-input"
                wrapperClassName="v3-datepicker-inner"
                calendarClassName="v3-datepicker"
                popperClassName="v3-datepicker-popper"
                showPopperArrow={false}
                portalId="v3-datepicker-portal"
                popperPlacement="bottom-start"
                todayButton={t('Today')}
            />
            <Calendar size={15} className="v3-datepicker-ico" aria-hidden="true" />
        </div>
    );
};

export default ReportDateInput;
